/**
 * Manual product comparator.
 *
 * The shopper marks products with the "Comparar" buttons (rendered by the
 * 'compare-button' snippet). Each product page / card also prints a JSON data
 * island (snippet 'product-compare-specs') with the technical specs parsed from
 * the per-variant "información nutricional" metaobject. This script keeps the
 * selection in localStorage and builds a side-by-side table — no extra requests.
 */
(function () {
  "use strict";

  var STORAGE_KEY = "keep:compare";

  function normalizeKey(label) {
    return (label || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "")
      .trim();
  }

  /* Pretty-print a raw key/label, e.g. "uso_diario" -> "Uso diario".
     Applied at render time so items already stored in localStorage with the
     raw key (from before this change) still display nicely. */
  function humanizeLabel(label) {
    var s = String(label == null ? "" : label).replace(/[_-]+/g, " ").trim();
    if (!s) return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  var CHECK_SVG =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
  var CROSS_SVG =
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>';

  /* Recognise boolean-ish values so they render as a check / cross icon
     instead of raw "true"/"false" text. Returns 'yes', 'no' or null. */
  function boolKind(value) {
    var n = normalizeKey(value);
    if (n === "true" || n === "si" || n === "yes" || n === "1" || n === "verdadero") return "yes";
    if (n === "false" || n === "no" || n === "0" || n === "falso") return "no";
    return null;
  }

  function renderCell(value) {
    var kind = boolKind(value);
    if (kind === "yes") {
      return '<td class="compare-table__cell--bool"><span class="compare-bool compare-bool--yes" role="img" aria-label="S\u00ed">' + CHECK_SVG + "</span></td>";
    }
    if (kind === "no") {
      return '<td class="compare-table__cell--bool"><span class="compare-bool compare-bool--no" role="img" aria-label="No">' + CROSS_SVG + "</span></td>";
    }
    return "<td>" + (value ? escapeHtml(value) : "\u2014") + "</td>";
  }

  function readState() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeState(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* storage unavailable — comparator just won't persist */
    }
  }

  function getProductData(id) {
    var el = document.querySelector(
      'script.compare-data[data-compare-product="' + id + '"]'
    );
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /* Resolve which variant to capture: the one currently selected in the product
     form if the button lives inside a product section, otherwise the default. */
  function resolveVariantId(button, data) {
    var section = button.closest("[js-product-section]");
    if (section) {
      var input = section.querySelector('input[name="id"]');
      if (input && input.value && data.variants[input.value]) {
        return input.value;
      }
    }
    if (data.variants[data.defaultVariantId]) return data.defaultVariantId;
    var keys = Object.keys(data.variants || {});
    return keys.length ? keys[0] : null;
  }

  function buildItem(data, variantId) {
    var variant = data.variants[variantId];
    if (!variant) return null;
    var specs = (variant.specs || []).map(function (s) {
      return { label: s.label, value: s.value, key: normalizeKey(s.label) };
    });
    return {
      id: String(data.id),
      variantId: String(variantId),
      title: data.title,
      vendor: data.vendor || "",
      url: data.url,
      image: data.image,
      price: data.price,
      specs: specs,
    };
  }

  class CompareTray extends HTMLElement {
    connectedCallback() {
      this.maxItems = parseInt(this.dataset.max, 10) || 3;
      this.highlightDiff = this.dataset.highlightDiff !== "false";
      this.onlyDiff = false;
      this.viewItems = null;

      this.bar = this.querySelector("[data-compare-bar]");
      this.barItems = this.querySelector("[data-compare-bar-items]");
      this.countEl = this.querySelector("[data-compare-count]");
      this.modal = this.querySelector("[data-compare-modal]");
      this.tableEl = this.querySelector("[data-compare-table]");
      this.diffToggle = this.querySelector("[data-compare-diff-toggle]");

      this.removeUnavailable();
      this.refreshFromDom();
      this.hidden = false;

      // Toggle buttons (event-delegated so it covers dynamically loaded cards).
      document.addEventListener("click", this.onToggleClick.bind(this));
      document.addEventListener("click", this.onCompareAllClick.bind(this));
      this.updateCompareAllButtons();

      this.addEventListener("click", this.onTrayClick.bind(this));
      if (this.diffToggle) {
        this.diffToggle.addEventListener("change", function (e) {
          this.onlyDiff = e.target.checked;
          this.renderTable();
        }.bind(this));
      }
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape") this.closeModal();
      }.bind(this));

      // Keep buttons/bar in sync across tabs.
      window.addEventListener("storage", function (e) {
        if (e.key === STORAGE_KEY) this.syncAll();
      }.bind(this));

      this.syncAll();
    }

    /* Re-read each stored product from its on-page JSON island (when present)
       so edits to the metafield — e.g. a new "titulo" or changed specs — show
       up without the shopper having to clear and re-add. Products whose card
       isn't on the current page keep their stored copy untouched. */
    refreshFromDom() {
      var items = readState();
      var changed = false;
      var next = items.map(function (it) {
        var data = getProductData(it.id);
        if (!data) return it;
        var variantId = data.variants && data.variants[it.variantId] ? it.variantId : data.defaultVariantId;
        var fresh = buildItem(data, variantId);
        if (fresh && fresh.specs.length) {
          changed = true;
          return fresh;
        }
        return it;
      });
      if (changed) writeState(next);
    }

    /* Drop stored products whose data island is no longer reachable is risky
       (other pages don't have it), so only drop entries with no specs at all. */
    removeUnavailable() {
      var items = readState().filter(function (it) {
        return it && it.specs && it.specs.length;
      });
      writeState(items);
    }

    onToggleClick(e) {
      var button = e.target.closest("[data-compare-toggle]");
      if (!button) return;
      e.preventDefault();
      this.toggle(button);
    }

    /* "Compara las sales": open the modal with every product on the page that
       has comparison active (i.e. printed a compare-data JSON island), without
       touching the shopper's manual selection in localStorage. */
    onCompareAllClick(e) {
      var button = e.target.closest("[data-compare-all]");
      if (!button) return;
      e.preventDefault();
      var items = this.collectAllOnPage();
      if (items.length < 2) {
        this.flash("No hay suficientes sales con comparación activa en esta página.");
        return;
      }
      this.openModalWith(items);
    }

    /* Build an item for every distinct compare-data island present on the page. */
    collectAllOnPage() {
      var seen = {};
      var items = [];
      document
        .querySelectorAll("script.compare-data[data-compare-product]")
        .forEach(function (el) {
          var id = el.getAttribute("data-compare-product");
          if (!id || seen[id]) return;
          var data = getProductData(id);
          if (!data) return;
          var variantId =
            data.variants && data.variants[data.defaultVariantId]
              ? data.defaultVariantId
              : Object.keys(data.variants || {})[0];
          var item = variantId && buildItem(data, variantId);
          if (item && item.specs.length) {
            seen[id] = true;
            items.push(item);
          }
        });
      return items;
    }

    /* Reveal the "Compara las sales" buttons only when there are at least two
       comparable products on the current page. */
    updateCompareAllButtons() {
      var count = this.collectAllOnPage().length;
      document.querySelectorAll("[data-compare-all]").forEach(function (btn) {
        btn.hidden = count < 2;
      });
    }

    onTrayClick(e) {
      if (e.target.closest("[data-compare-open]")) return this.openModal();
      if (e.target.closest("[data-compare-close]")) return this.closeModal();
      if (e.target.closest("[data-compare-clear]")) return this.clear();
      var remove = e.target.closest("[data-compare-remove]");
      if (remove) return this.removeById(remove.dataset.compareRemove);
    }

    toggle(button) {
      var id = String(button.dataset.compareId);
      var items = readState();
      var idx = items.findIndex(function (it) {
        return it.id === id;
      });

      if (idx > -1) {
        items.splice(idx, 1);
        writeState(items);
        this.syncAll();
        return;
      }

      if (items.length >= this.maxItems) {
        this.flash("Puedes comparar hasta " + this.maxItems + " productos.");
        return;
      }

      var data = getProductData(id);
      if (!data) {
        this.flash("Este producto no tiene datos técnicos para comparar.");
        return;
      }
      var variantId = resolveVariantId(button, data);
      var item = variantId && buildItem(data, variantId);
      if (!item || !item.specs.length) {
        this.flash("Este producto no tiene datos técnicos para comparar.");
        return;
      }
      items.push(item);
      writeState(items);
      this.syncAll();
    }

    removeById(id) {
      // While viewing a transient "Compara las sales" set, removing only edits
      // that view and never the shopper's stored selection.
      if (this.viewItems) {
        this.viewItems = this.viewItems.filter(function (it) {
          return it.id !== String(id);
        });
        if (this.viewItems.length < 2) this.closeModal();
        else this.renderTable();
        return;
      }
      var items = readState().filter(function (it) {
        return it.id !== String(id);
      });
      writeState(items);
      this.syncAll();
      if (!items.length) this.closeModal();
      else if (this.modal && !this.modal.hidden) this.renderTable();
    }

    clear() {
      writeState([]);
      this.syncAll();
      this.closeModal();
    }

    /* Re-render bar + sync every toggle button on the page. */
    syncAll() {
      var items = readState();
      var ids = items.map(function (it) {
        return it.id;
      });

      document.querySelectorAll("[data-compare-toggle]").forEach(function (btn) {
        var active = ids.indexOf(String(btn.dataset.compareId)) > -1;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-pressed", active ? "true" : "false");
        var labelEl = btn.querySelector("[data-compare-label]");
        if (labelEl) {
          labelEl.textContent = active
            ? btn.dataset.labelRemove || "Quitar"
            : btn.dataset.labelAdd || "Comparar";
        }
      });

      this.renderBar(items);
    }

    renderBar(items) {
      if (this.countEl) this.countEl.textContent = items.length;
      if (this.barItems) {
        this.barItems.innerHTML = items
          .map(function (it) {
            var img = it.image
              ? '<img src="' + it.image + '" alt="" loading="lazy">'
              : "";
            return (
              '<div class="compare-bar__item" title="' +
              escapeHtml(it.title) +
              '">' +
              img +
              '<button type="button" class="compare-bar__remove" data-compare-remove="' +
              it.id +
              '" aria-label="Quitar">&times;</button>' +
              "</div>"
            );
          })
          .join("");
      }
      if (this.bar) this.bar.hidden = items.length === 0;
      var openBtn = this.querySelector("[data-compare-open]");
      if (openBtn) openBtn.disabled = items.length < 2;
    }

    openModal() {
      this.viewItems = null;
      var items = readState();
      if (items.length < 2) {
        this.flash("Añade al menos 2 productos para comparar.");
        return;
      }
      this.renderTable();
      if (this.modal) this.modal.hidden = false;
      document.documentElement.classList.add("compare-modal-open");
    }

    /* Open the modal showing an explicit list of items (e.g. all active salts),
       leaving the stored manual selection untouched. */
    openModalWith(items) {
      if (!items || items.length < 2) return;
      this.viewItems = items;
      this.renderTable();
      if (this.modal) this.modal.hidden = false;
      document.documentElement.classList.add("compare-modal-open");
    }

    closeModal() {
      this.viewItems = null;
      if (this.modal) this.modal.hidden = true;
      document.documentElement.classList.remove("compare-modal-open");
    }

    renderTable() {
      var items = this.viewItems || readState();
      if (!this.tableEl || items.length < 2) return;

      // Union of spec keys, in first-seen order, keeping a display label.
      var order = [];
      var labels = {};
      items.forEach(function (it) {
        it.specs.forEach(function (s) {
          if (!s.key) return;
          if (!(s.key in labels)) {
            labels[s.key] = humanizeLabel(s.label);
            order.push(s.key);
          }
        });
      });

      var valueOf = function (item, key) {
        var match = item.specs.find(function (s) {
          return s.key === key;
        });
        return match && match.value ? match.value : "";
      };

      var rowsHtml = order
        .map(function (key) {
          var values = items.map(function (it) {
            return valueOf(it, key);
          });
          var distinct = values
            .map(function (v) {
              return normalizeKey(v);
            })
            .filter(function (v, i, a) {
              return v && a.indexOf(v) === i;
            });
          var isDiff = distinct.length > 1;
          if (this.onlyDiff && !isDiff) return "";
          var cells = values.map(renderCell).join("");
          var cls =
            isDiff && this.highlightDiff ? ' class="compare-table__row--diff"' : "";
          return (
            "<tr" +
            cls +
            '><th scope="row">' +
            escapeHtml(labels[key]) +
            "</th>" +
            cells +
            "</tr>"
          );
        }, this)
        .join("");

      var headHtml = items
        .map(function (it) {
          var img = it.image
            ? '<img src="' + it.image + '" alt="" loading="lazy">'
            : "";
          var price = it.price
            ? '<span class="compare-table__price">' + escapeHtml(it.price) + "</span>"
            : "";
          return (
            '<th scope="col"><a href="' +
            it.url +
            '" class="compare-table__product">' +
            img +
            '<span class="compare-table__name">' +
            escapeHtml(it.title) +
            "</span>" +
            price +
            "</a>" +
            '<button type="button" class="compare-table__remove" data-compare-remove="' +
            it.id +
            '" aria-label="Quitar">&times;</button>' +
            "</th>"
          );
        })
        .join("");

      this.tableEl.innerHTML =
        '<div class="compare-table__scroll"><table class="compare-table"><thead><tr><th scope="col" class="compare-table__corner"></th>' +
        headHtml +
        "</tr></thead><tbody>" +
        (rowsHtml ||
          '<tr><td colspan="' +
            (items.length + 1) +
            '">No hay diferencias entre los productos seleccionados.</td></tr>') +
        "</tbody></table></div>";
    }

    flash(message) {
      var el = document.createElement("div");
      el.className = "compare-flash";
      el.textContent = message;
      this.appendChild(el);
      setTimeout(function () {
        el.classList.add("is-visible");
      }, 10);
      setTimeout(function () {
        el.classList.remove("is-visible");
        setTimeout(function () {
          el.remove();
        }, 300);
      }, 2600);
    }
  }

  function escapeHtml(str) {
    return String(str == null ? "" : str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  if (!customElements.get("compare-tray")) {
    customElements.define("compare-tray", CompareTray);
  }
})();
